/* eslint-disable @typescript-eslint/no-explicit-any */
import context from "core/tv-guide.context";
import { useEffect, useRef, useLayoutEffect } from "react";
import { observer } from "mobx-react-lite";
import GridStore, { DimensionBlock } from "core/providers/grid.store";

type ChannelBlockProps = {
    store: GridStore;
    dimensionBlock: DimensionBlock | undefined;
    scrollParams: number[] | undefined;
}

const ChannelBlock = observer((props: ChannelBlockProps) => {
    // Refs
    const timeChunk = useRef<any>(null);
    const channelBlock = useRef<any>(null);
    const gridMainRef = useRef<any>(null);

    const { store, dimensionBlock, scrollParams } = props;

    useEffect(() => {
        if (!dimensionBlock || !scrollParams) {
            return;
        }

        const scrollIndexBuffers = [2, 4];
        const [topScrollPosition, leftScrollPosition] = scrollParams;

        // Find the starting indexes of the visible blocks
        let gridIndexStart = Math.round(leftScrollPosition / dimensionBlock.gridWidth);
        let horizontalStart = Math.round(leftScrollPosition / dimensionBlock.timeChunk.width);
        let verticalStart = Math.round(topScrollPosition / dimensionBlock.channelBlock.height);

        // Create a buffer of blocks behind the current index
        gridIndexStart = gridIndexStart <= 1 ? 0 : gridIndexStart - 1;
        horizontalStart = horizontalStart <= 1 ? 0 : horizontalStart - 1;
        verticalStart = verticalStart <= scrollIndexBuffers[1]
            ? 0
            : verticalStart - scrollIndexBuffers[1];

        // Find the end index of the visible blocks
        const gridIndexEnd = gridIndexStart + 2;
        const horizontalEnd = horizontalStart + dimensionBlock.indexSpread[1] + scrollIndexBuffers[0];
        const verticalEnd = verticalStart + dimensionBlock.indexSpread[1] + scrollIndexBuffers[1];

        store.updateGridScrollIndexes([gridIndexStart, gridIndexEnd]);
        store.updateVerticalScrollIndexes([verticalStart, verticalEnd]);
        store.updateHorizontalScrollIndexes([horizontalStart, horizontalEnd]);
    }, [dimensionBlock, scrollParams, store]);

    useLayoutEffect(() => {
        if (!timeChunk.current ||
            !channelBlock.current ||
            !gridMainRef.current ||
            props.dimensionBlock !== undefined) {
            return;
        }

        const rect = {
            grid: gridMainRef.current.getBoundingClientRect(),
            time: timeChunk.current.getBoundingClientRect(),
            channel: channelBlock.current.getBoundingClientRect()
        };

        const heightWithMargin = rect.channel.height + 4;

        const indexSpread = [Math.round(rect.grid.width / rect.time.width),
        // + 4 to account for margins
        context.channelTags ?
            Math.round((rect.channel.height * 8) / heightWithMargin) :
            Math.round(rect.grid.height / heightWithMargin)
        ];

        /**
         * Calculate the amount of pixels in a minute, then divide the
         * amount of milliseconds in a minute
         * */
        const pixelMinuteMultiplier = 60000 / (rect.time.width / 30);

        const dimensionBlock = {
            timeChunk: rect.time,
            pixelMinuteMultiplier: pixelMinuteMultiplier,
            channelBlock: rect.channel,
            gridMain: rect.grid,
            indexSpread: indexSpread,
            minuteWidth: rect.time.width / 30,
            gridWidth: rect.time.width * 48,
            viewWidth: rect.time.width * 8
        };

        store.updateDimensionBlock(dimensionBlock);
    }, [store, props.dimensionBlock, gridMainRef]);

    return (
        <>
            <div className="time-chunk position-absolute" ref={timeChunk}></div>
            <div
                className="channel-block floating-block channel-block-hidden position-absolute"
                id="channelBlock"
                ref={channelBlock}>
                {context.dictionary.channels}
            </div>
            <div
                className={`grid-main-panel grabbable position-absolute ${context.channelTags ? "grid-trim" : ""}`}
                id="gridMain"
                ref={gridMainRef}
            ></div>
        </>
    );
});

export default ChannelBlock;
